import { useMemo } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useTranslation } from "@repo/core";
import LoadingDrawdown from "./LoadingDrawdown";
import MissingDrawdown from "./MissingDrawdown";

interface DrawdownDataPoint {
  date: string;
  drawdown: number;
}

interface DrawdownsChartProps {
  drawdowns: DrawdownDataPoint[];
  isLoading?: boolean;
  className?: string;
}

/**
 * Drawdowns chart component showing drawdown analysis and metrics
 */
export const DrawdownsChart = ({
  drawdowns,
  isLoading = false,
  className = "",
}: DrawdownsChartProps) => {
  const { tDrawdowns, i18n } = useTranslation();

  // Prepare chart data in the format ApexCharts expects
  const chartSeries =
    drawdowns && drawdowns.length > 0
      ? [
        {
          name: tDrawdowns("seriesName"),
          data: drawdowns.map((point) => {
            const date = new Date(point.date);
            // Ensure valid date and number
            const timestamp = isNaN(date.getTime())
              ? Date.now()
              : date.getTime();
            const value =
              typeof point.drawdown === "number" && !isNaN(point.drawdown)
                ? point.drawdown
                : 0;

            return {
              x: timestamp,
              y: value,
            };
          }),
        },
      ]
      : [];

  // Drawdown chart configuration
  const chartOptions: ApexOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        background: "transparent",
        animations: {
          enabled: false, // Disable animations to prevent rendering issues
        },
        defaultLocale: i18n.language === 'cs' ? 'cs' : 'en',
        locales: [
          {
            name: 'en',
            options: {
              months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
              shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
              shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
              toolbar: {
                download: 'Download SVG',
                selection: 'Selection',
                selectionZoom: 'Selection Zoom',
                zoomIn: 'Zoom In',
                zoomOut: 'Zoom Out',
                pan: 'Panning',
                reset: 'Reset Zoom'
              }
            }
          },
          {
            name: 'cs',
            options: {
              months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
              shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
              days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
              shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
              toolbar: {
                download: 'Stáhnout SVG',
                selection: 'Výběr',
                selectionZoom: 'Výběr pro zoom',
                zoomIn: 'Přiblížit',
                zoomOut: 'Oddálit',
                pan: 'Posun',
                reset: 'Reset zoomu'
              }
            }
          }
        ]
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      colors: ["#3B82F6"],
      xaxis: {
        type: "datetime",
        labels: {
          format: "MMM yyyy",
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `${value.toFixed(1)}%`,
        },
      },
      grid: {
        borderColor: "#e5e7eb",
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      tooltip: {
        y: {
          formatter: (value) => `${value.toFixed(2)}%`,
        },
      },
    };
  }, [i18n.language]);

  if (isLoading) {
    return <LoadingDrawdown className={className} />;
  }

  if (!drawdowns || drawdowns.length === 0) {
    return <MissingDrawdown className={className} />;
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
      data-tour="drawdowns-chart"
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {tDrawdowns("title")}
      </h2>

      {/* Horizontal divider */}
      <hr className="border-gray-200 mb-6" />

      {/* Drawdown Chart Section */}
      <div className="h-96">
        {chartSeries.length > 0 && chartSeries[0].data.length > 0 ? (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height="100%"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="text-4xl mb-2">📈</div>
              <p>No chart data available</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawdownsChart;
